import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Tabs,
  Tab,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  CalendarToday,
  CalendarMonth,
  DateRange,
  Business,
  Person,
  FileDownload,
  Assessment,
  AdminPanelSettings
} from '@mui/icons-material';
import { authAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface Project {
  id: string;
  organization: string;
  name: string;
  location: string;
  createdBy: string;
  creationTime: number;
  LastUpdatedBy: string;
  LastUpdatedTime: number;
}

interface ProjectAnalytics {
  totalProjects?: number;
  projectsThisMonth?: number;
  projectsToday?: number;
  projectsThisYear?: number;
  projectsByOrganization?: Array<{ 
    organization?: string; 
    total?: number;
    thisMonth?: number;
    thisYear?: number;
    today?: number;
  }>;
  projectsByUser?: Array<{ user?: string; count?: number }>;
  projectsByMonth?: Array<{ month?: string; count?: number }>;
  recentProjects?: Array<{
    id?: string;
    organization?: string;
    name?: string;
    location?: string;
    createdBy?: string;
    creationTime?: number;
    lastUpdatedBy?: string;
    lastUpdatedTime?: number;
  }>;
  exportStats?: {
    totalExports?: number;
    exportsThisMonth?: number;
    exportsToday?: number;
  };
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658'];

const ProjectAnalytics: React.FC = () => {
  const [analytics, setAnalytics] = useState<ProjectAnalytics | null>(null);
  const [projects, setProjects] = useState<Array<{
    id?: string;
    organization?: string;
    name?: string;
    location?: string;
    createdBy?: string;
    creationTime?: number;
    lastUpdatedBy?: string;
    lastUpdatedTime?: number;
  }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | '1y'>('30d');
  const [activeTab, setActiveTab] = useState(0);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const { organization, user } = useAuth();

  useEffect(() => {
    fetchProjectData();
  }, [timeRange, dateRange]);

  const fetchProjectData = async () => {
    try {
      setLoading(true);
      
      // Convert date range to epoch milliseconds
      const startDate = dateRange[0] ? Math.floor(dateRange[0].getTime() / 1000) : undefined;
      const endDate = dateRange[1] ? Math.floor(dateRange[1].getTime() / 1000) : undefined;
      
      // Fetch real project analytics from the database with date filtering
      const analytics = await authAPI.getProjectAnalytics(startDate, endDate);
      setAnalytics(analytics);
      
      // Set projects for the recent projects display
      setProjects(analytics.recentProjects || []);
      
    } catch (err: any) {
      setError('Failed to load project analytics');
      console.error('Project analytics error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString();
  };

  const formatDateTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  // Get chart data (now properly filtered by backend)
  const getFilteredChartData = () => {
    if (!analytics?.projectsByOrganization) return [];
    
    // Backend now handles the filtering, so we just return the data
    return analytics.projectsByOrganization;
  };

  // Get user chart data
  const getUserChartData = () => {
    if (!analytics?.projectsByUser) return [];
    
    // Backend now handles the filtering, so we just return the data
    return analytics.projectsByUser;
  };

  // Get organization totals grouped by organization
  const getOrganizationTotals = () => {
    if (!analytics?.projectsByUser || !analytics?.projectsByOrganization) return [];
    
    // Group users by organization
    const userMap = new Map<string, Array<{user: string, count: number, organization: string}>>();
    analytics.projectsByUser.forEach((user: any) => {
      const org = user.organization || 'Unknown';
      if (!userMap.has(org)) {
        userMap.set(org, []);
      }
      userMap.get(org)!.push(user);
    });
    
    // Create organization totals
    const orgTotals = analytics.projectsByOrganization.map(org => ({
      organization: org.organization || 'Unknown',
      totalProjects: org.total || 0,
      users: userMap.get(org.organization || 'Unknown') || []
    }));
    
    return orgTotals;
  };

  // Get date range label for display
  const getDateRangeLabel = () => {
    if (!dateRange[0] || !dateRange[1]) {
      return 'All Time';
    }
    
    const startDate = dateRange[0].toLocaleDateString();
    const endDate = dateRange[1].toLocaleDateString();
    return `${startDate} - ${endDate}`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!analytics) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No project data available
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" component="h1">
            Project Analytics
          </Typography>
          {user?.username === 'Roi' && (
            <Chip
              icon={<AdminPanelSettings />}
              label="Admin View"
              color="primary"
              variant="outlined"
              size="small"
            />
          )}
        </Box>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Time Range</InputLabel>
          <Select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as any)}
            label="Time Range"
          >
            <MenuItem value="7d">Last 7 days</MenuItem>
            <MenuItem value="30d">Last 30 days</MenuItem>
            <MenuItem value="90d">Last 90 days</MenuItem>
            <MenuItem value="1y">Last year</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {user?.username === 'Roi' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Admin View:</strong> You are viewing analytics for all projects across all organizations in your AWS account. 
            Regular users only see their organization's projects.
          </Typography>
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Today
                  </Typography>
                  <Typography variant="h4">
                    {analytics.projectsToday || 0}
                  </Typography>
                </Box>
                <CalendarToday color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    This Month
                  </Typography>
                  <Typography variant="h4">
                    {analytics.projectsThisMonth || 0}
                  </Typography>
                </Box>
                <CalendarMonth color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    This Year
                  </Typography>
                  <Typography variant="h4">
                    {analytics.projectsThisYear || 0}
                  </Typography>
                </Box>
                <DateRange color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Date Range Picker */}
      <Box sx={{ mb: 3 }}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Filter by Date Range
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              label="Start Date"
              type="date"
              value={dateRange[0] ? dateRange[0].toISOString().split('T')[0] : ''}
              onChange={(e) => {
                e.preventDefault();
                const date = e.target.value ? new Date(e.target.value) : null;
                setDateRange([date, dateRange[1]]);
              }}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              size="small"
              sx={{ minWidth: 200 }}
            />
            <Typography variant="body2" sx={{ mx: 1 }}>
              to
            </Typography>
            <TextField
              label="End Date"
              type="date"
              value={dateRange[1] ? dateRange[1].toISOString().split('T')[0] : ''}
              onChange={(e) => {
                e.preventDefault();
                const date = e.target.value ? new Date(e.target.value) : null;
                setDateRange([dateRange[0], date]);
              }}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              size="small"
              sx={{ minWidth: 200 }}
            />
          </Box>
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                const today = new Date();
                const yesterday = new Date(today);
                yesterday.setDate(yesterday.getDate() - 1);
                setDateRange([yesterday, today]);
              }}
            >
              Yesterday
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                const today = new Date();
                const weekAgo = new Date(today);
                weekAgo.setDate(weekAgo.getDate() - 7);
                setDateRange([weekAgo, today]);
              }}
            >
              Last 7 Days
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                const today = new Date();
                const monthAgo = new Date(today);
                monthAgo.setDate(monthAgo.getDate() - 30);
                setDateRange([monthAgo, today]);
              }}
            >
              Last 30 Days
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                const today = new Date();
                const yearAgo = new Date(today);
                yearAgo.setFullYear(yearAgo.getFullYear() - 1);
                setDateRange([yearAgo, today]);
              }}
            >
              Last Year
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setDateRange([null, null])}
            >
              Clear
            </Button>
          </Box>
            {dateRange[0] && dateRange[1] && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Showing data for: {getDateRangeLabel()}
                {loading && <span> (Loading...)</span>}
              </Typography>
            )}
        </Paper>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Overview" />
          <Tab label="By Organization" />
          <Tab label="By User" />
          <Tab label="Timeline" />
          <Tab label="Recent Projects" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Projects by Month
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={analytics.projectsByMonth || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="count" stroke="#8884d8" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Export Statistics
                </Typography>
                <Box display="flex" flexDirection="column" gap={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography>Total Exports</Typography>
                    <Chip label={analytics.exportStats?.totalExports || 0} color="primary" />
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography>This Month</Typography>
                    <Chip label={analytics.exportStats?.exportsThisMonth || 0} color="secondary" />
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography>Today</Typography>
                    <Chip label={analytics.exportStats?.exportsToday || 0} color="success" />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Projects by Organization {dateRange[0] && dateRange[1] ? `(${getDateRangeLabel()})` : '(All Time)'}
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={getFilteredChartData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="organization" />
                <YAxis />
                <Tooltip 
                  formatter={(value, name) => [
                    `${value} projects`, 
                    getDateRangeLabel()
                  ]}
                />
                <Legend />
                <Bar 
                  dataKey="total" 
                  fill="#8884d8" 
                  name={getDateRangeLabel()}
                  label={{ position: 'top', fill: '#666' }}
                />
              </BarChart>
            </ResponsiveContainer>
            
            {/* Detailed Organization Breakdown Table */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Detailed Organization Breakdown
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Organization</strong></TableCell>
                      <TableCell align="right"><strong>Projects</strong></TableCell>
                      <TableCell><strong>Details</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getFilteredChartData().map((org) => (
                      <TableRow key={org.organization || 'unknown'}>
                        <TableCell>{org.organization || 'Unknown'}</TableCell>
                        <TableCell align="right">
                          {org.total || 0}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={`Today: ${org.today || 0}`} 
                            size="small" 
                            sx={{ mr: 1, mb: 1 }}
                            color={(org.today || 0) > 0 ? "primary" : "default"}
                          />
                          <Chip 
                            label={`This Month: ${org.thisMonth || 0}`} 
                            size="small" 
                            sx={{ mr: 1, mb: 1 }}
                            color={(org.thisMonth || 0) > 0 ? "secondary" : "default"}
                          />
                          <Chip 
                            label={`This Year: ${org.thisYear || 0}`} 
                            size="small" 
                            sx={{ mr: 1, mb: 1 }}
                            color={(org.thisYear || 0) > 0 ? "success" : "default"}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </CardContent>
        </Card>
      )}

      {activeTab === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Projects by User {dateRange[0] && dateRange[1] ? `(${getDateRangeLabel()})` : '(All Time)'}
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={getUserChartData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="user" />
                <YAxis />
                <Tooltip 
                  formatter={(value, name) => [
                    `${value} projects`, 
                    getDateRangeLabel()
                  ]}
                />
                <Legend />
                <Bar 
                  dataKey="count" 
                  fill="#8884d8" 
                  name={getDateRangeLabel()}
                  label={{ position: 'top', fill: '#666' }}
                />
              </BarChart>
            </ResponsiveContainer>
            
            {/* Organization Totals Table */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Organization Totals
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Organization</strong></TableCell>
                      <TableCell align="right"><strong>Total Projects</strong></TableCell>
                      <TableCell><strong>Users</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getOrganizationTotals().map((org) => (
                      <TableRow key={org.organization}>
                        <TableCell>{org.organization}</TableCell>
                        <TableCell align="right">{org.totalProjects}</TableCell>
                        <TableCell>
                          {org.users.map((user: {user: string, count: number, organization: string}, index: number) => (
                            <Chip 
                              key={index}
                              label={`${user.user || 'Unknown'}: ${user.count || 0}`} 
                              size="small" 
                              sx={{ mr: 1, mb: 1 }}
                              color="primary"
                              variant="outlined"
                            />
                          ))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </CardContent>
        </Card>
      )}

      {activeTab === 3 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Project Timeline
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={analytics.projectsByMonth || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="count" stroke="#8884d8" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {activeTab === 4 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Recent Projects
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Project Name</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Created By</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Last Updated</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analytics.recentProjects?.map((project) => (
                    <TableRow key={project.id || 'unknown'}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {project.name || 'Unknown'}
                        </Typography>
                      </TableCell>
                      <TableCell>{project.location || 'Unknown'}</TableCell>
                      <TableCell>{project.createdBy || 'Unknown'}</TableCell>
                      <TableCell>{project.creationTime ? formatDateTime(project.creationTime) : 'Unknown'}</TableCell>
                      <TableCell>{project.lastUpdatedTime ? formatDateTime(project.lastUpdatedTime) : 'Unknown'}</TableCell>
                    </TableRow>
                  )) || []}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ProjectAnalytics;
